import React, { useEffect, useRef } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { CheckIcon } from '@arcade/web/icons/react'
import { breakpoints, Text } from '@cash-design-system/react'
import { StatusInterstitialBlocker } from '@cash-web/protos/squareup/franklin/blockers/status_interstitial_blocker.pb'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../../BaseBlocker/BaseBlocker'

interface Props extends BlockerProps {
  blocker: StatusInterstitialBlocker
}

const animationDuration = 1 // seconds
const scheduledActionDelay = animationDuration * 1000 + 1000 // 1 second after animation finishes, in ms

const ContentCentered = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;

  /* same height as GrantFlow container */
  height: calc(100dvh - 12rem);
  ${breakpoints.tablet} {
    height: 100%;
  }
`

const Spacer = styled.div`
  height: ${({ theme }) => theme.spacing.m};
`

const IconAnimation = styled.div`
  opacity: 0;
  animation: fade-in-scale ${animationDuration}s ease-out forwards;

  @keyframes fade-in-scale {
    0% {
      opacity: 0;
      transform: scale(0.5);
    }

    50% {
      opacity: 1;
      transform: scale(1.1);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const TextAnimation = styled.div`
  opacity: 0;
  animation: slide-up ${animationDuration}s ease-out 0.3s forwards;

  @keyframes slide-up {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

// A duplicated IconMat that maintains a white color for the check icon
// By default IconMat uses the button tint color for the checkmark, but we're overriding
// that to be black in this page, so we need a separate component to maintain the white color.
const Circle = styled.div`
  color: ${props => props.theme.colors.cardBackground};
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  width: ${props => props.theme.pxToRem(56)};
  height: ${props => props.theme.pxToRem(56)};
  display: grid;
  place-items: center;
`

export function BlockerStatusInterstitial({ blocker }: Props): React.ReactElement {
  const timer = useRef<number>()
  const form = useForm<BaseBlockerFormFields>()

  const flow = useFlowContext()

  useEffect(() => {
    if (timer.current) {
      return
    }

    timer.current = window.setTimeout(async () => {
      if (blocker.scheduled_action) {
        await flow.executeBlockerAction(blocker.scheduled_action)
      }
    }, scheduledActionDelay)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scheduled action is a static object
  }, [flow])

  return (
    <BaseBlocker
      onSubmit={() => {
        return
      }}
      form={form}
      buttons={[]}
    >
      <ContentCentered>
        <IconAnimation>
          <Circle>
            <CheckIcon size={24} />
          </Circle>
        </IconAnimation>
        <Spacer />
        <TextAnimation>
          <Text data-testid="success-text" typography="sectionTitle" as="h2" color="textStandard">
            {blocker.text}
          </Text>
        </TextAnimation>
      </ContentCentered>
    </BaseBlocker>
  )
}
